"use client"

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react"
import Link from "next/link"
import Marquee from "react-fast-marquee"
import { cn } from "@/lib/utils"

interface PProps {
    children: React.ReactNode
    className?: string
    containerClassName?: string
    speed?: number
    direction?: "left" | "right" | "up" | "down"
    truncatePosition?: 'end' | 'middle'
    marqueeAnimation?: boolean
    href?: string
}

export function P({
    children,
    className,
    containerClassName,
    speed = 50,
    direction = "left",
    truncatePosition = "end",
    marqueeAnimation = true,
    href
}: PProps) {
    const [isHovered, setIsHovered] = useState(false)
    const [shouldShowMarquee, setShouldShowMarquee] = useState(false)
    const [truncatedText, setTruncatedText] = useState<React.ReactNode>(children)
    const [needsTruncation, setNeedsTruncation] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLElement>(null)
    const isHoveredRef = useRef(false)

    // Extract text content from children for measurement
    const getTextContent = (node: React.ReactNode): string => {
        if (typeof node === 'string') return node
        if (typeof node === 'number') return node.toString()
        if (Array.isArray(node)) return node.map(getTextContent).join('')
        if (node && typeof node === 'object' && 'props' in node) {
            return getTextContent((node as any).props.children)
        }
        return ''
    }

    const textContent = getTextContent(children)

    // Simplified truncation calculation
    const calculateTruncation = useCallback(() => {
        if (!containerRef.current || !textRef.current) {
            // If refs aren't ready, show original content and try again soon
            setTruncatedText(children)
            setNeedsTruncation(false)
            return
        }
        
        const containerWidth = containerRef.current.clientWidth
        
        // If container isn't ready yet, show original content
        if (containerWidth <= 0) {
            setTruncatedText(children)
            setNeedsTruncation(false)
            return
        }
        
        // Create measurement element with same styles
        const tempSpan = document.createElement('span')
        tempSpan.style.cssText = `
            position: absolute;
            visibility: hidden;
            height: auto;
            width: auto;
            white-space: nowrap;
            top: -9999px;
            left: -9999px;
        `
        
        // Copy className to inherit styles
        if (className) {
            tempSpan.className = className
        }
        
        // Copy computed styles from the actual text element
        const computedStyle = window.getComputedStyle(textRef.current)
        tempSpan.style.fontSize = computedStyle.fontSize
        tempSpan.style.fontFamily = computedStyle.fontFamily
        tempSpan.style.fontWeight = computedStyle.fontWeight
        tempSpan.style.letterSpacing = computedStyle.letterSpacing
        
        document.body.appendChild(tempSpan)
        
        // Test if full text fits
        tempSpan.textContent = textContent
        const fullWidth = tempSpan.getBoundingClientRect().width
        
        if (fullWidth <= containerWidth) {
            // Text fits perfectly
            setTruncatedText(children)
            setNeedsTruncation(false)
            document.body.removeChild(tempSpan)
            return
        }
        
        // Text needs truncation
        setNeedsTruncation(true)
        
        if (truncatePosition === 'middle') {
            // Middle truncation
            let bestResult = '...'
            let maxChars = 0
            
            // Try different lengths to find the best fit
            for (let totalChars = 1; totalChars <= textContent.length; totalChars++) {
                const startChars = Math.floor(totalChars / 2)
                const endChars = totalChars - startChars
                
                if (startChars === 0 || endChars === 0) continue
                
                const startPart = textContent.slice(0, startChars)
                const endPart = textContent.slice(-endChars)
                const testText = startPart + '...' + endPart
                
                tempSpan.textContent = testText
                if (tempSpan.getBoundingClientRect().width <= containerWidth) {
                    bestResult = testText
                    maxChars = totalChars
                } else {
                    break
                }
            }
            
            setTruncatedText(bestResult)
        } else {
            // End truncation - simple and reliable
            let bestResult = '...'
            
            // Try progressively longer strings
            for (let i = 1; i <= textContent.length; i++) {
                const testText = textContent.slice(0, i) + '...'
                tempSpan.textContent = testText
                
                if (tempSpan.getBoundingClientRect().width <= containerWidth) {
                    bestResult = testText
                } else {
                    break
                }
            }
            
            setTruncatedText(bestResult)
        }
        
        document.body.removeChild(tempSpan)
    }, [children, textContent, className, truncatePosition])

    // Debounced calculation to avoid excessive calls during animations
    const debouncedCalculation = useCallback(() => {
        // Don't recalculate if we're currently hovering (prevents infinite loop)
        if (isHoveredRef.current) {
            return () => {}
        }
        
        const timeoutId = setTimeout(calculateTruncation, 100)
        return () => clearTimeout(timeoutId)
    }, [calculateTruncation])

    // Calculate on mount and dependency changes
    useLayoutEffect(() => {
        calculateTruncation()
    }, [calculateTruncation])

    // Handle hover state and marquee
    useEffect(() => {
        if (marqueeAnimation && isHovered && needsTruncation) {
            setShouldShowMarquee(true)
        } else {
            setShouldShowMarquee(false)
        }
    }, [isHovered, needsTruncation, marqueeAnimation])

    // Cleanup timer for forcing mouse leave
    useEffect(() => {
        let cleanupTimer: NodeJS.Timeout

        if (isHovered) {
            // Set a cleanup timer as a fallback
            cleanupTimer = setTimeout(() => {
                if (containerRef.current && !containerRef.current.matches(':hover')) {
                    setIsHovered(false)
                    isHoveredRef.current = false
                    setShouldShowMarquee(false)
                }
            }, 200)
        }

        return () => {
            if (cleanupTimer) {
                clearTimeout(cleanupTimer)
            }
        }
    }, [isHovered])

    // Enhanced mouse leave detection with native events
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        let leaveTimer: NodeJS.Timeout

        const handleMouseLeave = (e: MouseEvent) => {
            // Use native event to ensure we catch it
            const rect = container.getBoundingClientRect()
            const { clientX, clientY } = e
            
            // Check if mouse is actually outside the element
            const isOutside = clientX < rect.left || 
                            clientX > rect.right || 
                            clientY < rect.top || 
                            clientY > rect.bottom

            if (isOutside) {
                // Small delay to handle edge cases
                leaveTimer = setTimeout(() => {
                    setIsHovered(false)
                    isHoveredRef.current = false
                    setShouldShowMarquee(false)
                }, 50)
            }
        }

        const handleMouseEnter = () => {
            if (leaveTimer) {
                clearTimeout(leaveTimer)
            }
        }

        // Use native events for more reliable detection
        container.addEventListener('mouseleave', handleMouseLeave, true)
        container.addEventListener('mouseenter', handleMouseEnter, true)

        return () => {
            container.removeEventListener('mouseleave', handleMouseLeave, true)
            container.removeEventListener('mouseenter', handleMouseEnter, true)
            if (leaveTimer) {
                clearTimeout(leaveTimer)
            }
        }
    }, [])

    // Recalculate on window resize
    useEffect(() => {
        const handleResize = () => {
            // Don't recalculate during hover
            if (!isHoveredRef.current) {
                debouncedCalculation()
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [debouncedCalculation])

    // Recalculate when container becomes visible (for collapsible content)
    useEffect(() => {
        if (!containerRef.current) return

        const observer = new ResizeObserver(() => {
            // Don't recalculate during hover
            if (!isHoveredRef.current) {
                debouncedCalculation()
            }
        })

        observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [debouncedCalculation])

    return (
        <div
            ref={containerRef as React.RefObject<HTMLDivElement>}
            className={cn(
                "relative overflow-hidden",
                containerClassName
            )}
            onMouseEnter={() => {
                setIsHovered(true)
                isHoveredRef.current = true
            }}
            onMouseLeave={() => {
                setIsHovered(false)
                isHoveredRef.current = false
            }}
        >
            <div
                ref={textRef}
                className={cn(
                    "whitespace-nowrap overflow-hidden m-0 p-0",
                    className
                )}
                style={{ 
                    margin: 0, 
                    padding: 0
                }}
            >
                {marqueeAnimation && isHovered && shouldShowMarquee ? (
                    <Marquee
                        speed={speed}
                        // delay={2}
                        gradient={false}
                        direction={direction}
                        pauseOnHover={false}
                        className="!m-0 !p-0"
                        style={{ 
                            margin: 0, 
                            padding: 0
                        }}
                    >
                        <span className={className} style={{ marginRight: '2rem' }}>
                            {href ? (
                                <Link href={href} className="block w-full">
                                    {children}
                                </Link>
                            ) : (
                                children
                            )}
                        </span>
                    </Marquee>
                ) : (
                    href ? (
                        <Link href={href} className={cn("block w-full", className)}>
                            {truncatedText}
                        </Link>
                    ) : (
                        <span className={className}>
                            {truncatedText}
                        </span>
                    )
                )}
            </div>
        </div>
    )
}